/**
 * Custom Hashing Engine & Cryptographic Integrity Adapter
 * Aghora Abraham Global LLC | bhutadamarasena.com
 *
 * This module provides an extensible, pluggable interface for custom hashing algorithms.
 * When embedded into the main monolithic website at bhutadamarasena.com, developers
 * can plug in their exact proprietary hashing function either via direct registration,
 * the window.__AGHORA_CUSTOM_HASH_FN__ global hook, or environment secrets.
 */

import { tmd12Hash, TMD12_METADATA, type TMD12Digest } from "./tmd-hash-v12";

export * from "./tmd-hash-v12";

export type CustomHashFunction = (data: string, saltOrSecret?: string) => Promise<string> | string;
export type CustomHashVerifier = (data: string, hash: string, saltOrSecret?: string) => Promise<boolean> | boolean;

export interface SignedPayload<T = any> {
  data: T;
  timestamp: number;
  hash: string;
  algorithm: string;
  tmd12Digest?: TMD12Digest;
}

/**
 * Built-in high-entropy Aghora non-linear transform hashing function.
 * Used as the primary custom algorithm if no external custom override is provided.
 */
export function aghoraBitwiseHash(input: string, salt: string = "aghora_abraham_2026"): string {
  const combined = `${salt}:${input}:${salt.split("").reverse().join("")}`;
  let h1 = 0xdeadbeef ^ combined.length;
  let h2 = 0x41c6ce57 ^ combined.length;
  let h3 = 0x7fffffff ^ (combined.length * 31);
  let h4 = 0x811c9dc5 ^ (combined.length * 17);

  for (let i = 0; i < combined.length; i++) {
    const ch = combined.charCodeAt(i);
    // Round 1: FNV-1a variant with dynamic rotation
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h1 = ((h1 << 13) | (h1 >>> 19)) ^ h2;

    // Round 2: Murmur-inspired mix
    h2 = Math.imul(h2 ^ ch, 1597334677);
    h2 = ((h2 << 17) | (h2 >>> 15)) ^ h3;

    // Round 3: S-Box style non-linear transform
    h3 = Math.imul(h3 ^ (ch * 33), 2246822507);
    h3 = ((h3 << 11) | (h3 >>> 21)) ^ h4;

    // Round 4: Cross-diffusion
    h4 = Math.imul(h4 ^ (ch + i), 3266489909);
    h4 = ((h4 << 7) | (h4 >>> 25)) ^ h1;
  }

  // Final cascading avalanche rounds
  h1 ^= h1 >>> 16;
  h1 = Math.imul(h1, 2246822507);
  h1 ^= h1 >>> 13;

  h2 ^= h2 >>> 16;
  h2 = Math.imul(h2, 3266489909);
  h2 ^= h2 >>> 13;

  h3 ^= h3 >>> 15;
  h3 = Math.imul(h3, 2654435761);
  h3 ^= h3 >>> 16;

  h4 ^= h4 >>> 14;
  h4 = Math.imul(h4, 1597334677);
  h4 ^= h4 >>> 15;

  const hex1 = (h1 >>> 0).toString(16).padStart(8, "0");
  const hex2 = (h2 >>> 0).toString(16).padStart(8, "0");
  const hex3 = (h3 >>> 0).toString(16).padStart(8, "0");
  const hex4 = (h4 >>> 0).toString(16).padStart(8, "0");

  return `agh_${hex1}${hex2}${hex3}${hex4}`;
}

/**
 * Aghora 64-Bit Circular Rolling Bitwise Avalanche Hash
 * Exactly implements Definition 5.1 of Ancient Runes Monograph:
 * H_0 = 0x9E3779B97F4A7C15 (Golden Ratio Constant in 64-bit integer ring Z_{2^64})
 * h_{k+1} = ((h_k <<< 13) ^ (b_k * 0x517CC1B727220A95)) + (h_k >>> 29) mod 2^64
 * h_final = h_L ^ ((h_L >> 33) * 0xFF51AFD7ED558CCD) mod 2^64
 */
export function aghoraBitwiseHash64(input: string | Uint8Array): string {
  const bytes = typeof input === "string" ? new TextEncoder().encode(input) : input;
  const MASK_64 = 0xFFFFFFFFFFFFFFFFn;
  const MULT_BYTE = 0x517CC1B727220A95n;
  const AVALANCHE_MULT = 0xFF51AFD7ED558CCDn;

  let h = 0x9E3779B97F4A7C15n;

  for (let i = 0; i < bytes.length; i++) {
    const b = BigInt(bytes[i]);
    const rotl13 = ((h << 13n) | (h >> (64n - 13n))) & MASK_64;
    const byteTerm = (b * MULT_BYTE) & MASK_64;
    const rotr29 = (h >> 29n) & MASK_64;
    h = ((rotl13 ^ byteTerm) + rotr29) & MASK_64;
  }

  const shifted = (h >> 33n) & MASK_64;
  const avTerm = (shifted * AVALANCHE_MULT) & MASK_64;
  const hFinal = (h ^ avTerm) & MASK_64;

  return hFinal.toString(16).padStart(16, "0");
}

export class CustomHashingEngine {
  private static instance: CustomHashingEngine;
  private algorithmName: string = "aghora_custom_v1";
  private customHashFn: CustomHashFunction | null = null;
  private customVerifierFn: CustomHashVerifier | null = null;
  private defaultSalt: string = "aghora_abraham_global_bhutadamarasena";

  private constructor() {
    // Check if host monolithic application injected a custom hashing hook on window
    if (typeof window !== "undefined" && (window as any).__AGHORA_CUSTOM_HASH_FN__) {
      this.customHashFn = (window as any).__AGHORA_CUSTOM_HASH_FN__;
      this.algorithmName = (window as any).__AGHORA_CUSTOM_HASH_NAME__ || "host_custom_algorithm";
    }
  }

  public static getInstance(): CustomHashingEngine {
    if (!CustomHashingEngine.instance) {
      CustomHashingEngine.instance = new CustomHashingEngine();
    }
    return CustomHashingEngine.instance;
  }

  /**
   * Registers a user-defined custom hashing function from the main monolithic codebase.
   */
  public registerCustomAlgorithm(
    name: string,
    hashFn: CustomHashFunction,
    verifierFn?: CustomHashVerifier
  ): void {
    this.algorithmName = name;
    this.customHashFn = hashFn;
    this.customVerifierFn = verifierFn || null;
    console.info(`[CustomHashingEngine] Registered custom hashing algorithm: "${name}"`);
  }

  /**
   * Sets the default salt or HMAC secret used when generating hashes.
   */
  public setDefaultSalt(salt: string): void {
    this.defaultSalt = salt;
  }

  public getAlgorithmName(): string {
    return this.algorithmName;
  }

  /**
   * Computes hash for input string using the active custom hashing algorithm.
   */
  public async computeHash(data: string, saltOrSecret?: string): Promise<string> {
    const salt = saltOrSecret || this.defaultSalt;

    // 1. If host application or developer registered a custom function, use it
    if (this.customHashFn) {
      return await this.customHashFn(data, salt);
    }

    // 2. Check window hook again in case it was injected late by the monolith
    if (typeof window !== "undefined" && (window as any).__AGHORA_CUSTOM_HASH_FN__) {
      return await (window as any).__AGHORA_CUSTOM_HASH_FN__(data, salt);
    }

    // 3. Fall back to the built-in Aghora multi-round bitwise transform
    return aghoraBitwiseHash(data, salt);
  }

  /**
   * Verifies if an existing hash matches the raw input data.
   */
  public async verifyHash(data: string, hash: string, saltOrSecret?: string): Promise<boolean> {
    if (!hash) return false;
    const salt = saltOrSecret || this.defaultSalt;

    if (this.customVerifierFn) {
      return await this.customVerifierFn(data, hash, salt);
    }

    const calculated = await this.computeHash(data, salt);
    return calculated === hash;
  }

  /**
   * Creates a tamper-evident signed payload with timestamp, custom hash, and algorithm stamp.
   */
  public async signPayload<T = any>(data: T, saltOrSecret?: string): Promise<SignedPayload<T>> {
    const timestamp = Date.now();
    const rawString = JSON.stringify({ data, timestamp });
    const hash = await this.computeHash(rawString, saltOrSecret);

    return {
      data,
      timestamp,
      hash,
      algorithm: this.algorithmName,
    };
  }

  /**
   * Verifies integrity of a signed payload.
   */
  public async verifyPayload<T = any>(payload: SignedPayload<T>, saltOrSecret?: string): Promise<boolean> {
    if (!payload || !payload.hash || !payload.timestamp) return false;
    const rawString = JSON.stringify({ data: payload.data, timestamp: payload.timestamp });
    return await this.verifyHash(rawString, payload.hash, saltOrSecret);
  }
}

export const customHashing = CustomHashingEngine.getInstance();
